type ErrorWithMessage = { message?: string };
type ErrorWithResponse = { response?: { data?: ErrorWithMessage; message?: string } };
type ErrorWithData = { data?: ErrorWithMessage };
type ErrorWithError = { error?: ErrorWithMessage };
type ApiError = ErrorWithMessage & ErrorWithResponse & ErrorWithData & ErrorWithError;

export const extractApiErrorMessage = (error: unknown): string | null => {
  // 1) If it's already an Error with a plain message
  if (error instanceof Error && typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  // 2) Try common fetch/axios-like shapes
  if (typeof error === "object" && error !== null) {
    const err = error as ApiError;
    // e.g. error.response.data.message or error.data.message
    const potentialPaths: Array<(obj: ApiError) => unknown> = [
      (e) => e.response?.data?.message,
      (e) => e.response?.message,
      (e) => e.data?.message,
      (e) => e.message,
      (e) => e.error?.message,
    ];

    for (const getPath of potentialPaths) {
      const value = getPath(err);
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }
  }

  // 3) Try to parse JSON if it's a string containing JSON with message
  if (typeof error === "string") {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // ignore JSON parse errors
    }
  }

  return null;
};

export const extractApiErrorMessageOrDefault = (error: unknown, fallback: string): string => {
  return extractApiErrorMessage(error) ?? fallback;
};
